import { CmpInvoiceCategoryDefaultsFieldsFragment } from "../../../../graphql/types";
import {
  CmpInvoiceType,
  getCmpInvoiceTypeFromSearchParams,
  getSearchParamsForCmpInvoiceType,
} from "../../../../utils/cmpInvoiceNavigation";
import { MockInvoiceLineItem } from "../mockInvoiceData";

// Pure helpers for the Create Invoice page — kept side-effect-free so the
// catalog→line-item mapping and the chamber-setup-driven tax/account defaults
// can be unit tested in isolation.

export type InvoiceTab = CmpInvoiceType;

export const getInvoiceTabFromSearchParams = (
  searchParams: URLSearchParams,
): InvoiceTab => getCmpInvoiceTypeFromSearchParams(searchParams);

export const getSearchParamsForInvoiceTab = ({
  searchParams,
  tab,
}: {
  searchParams: URLSearchParams;
  tab: InvoiceTab;
}): URLSearchParams => {
  return getSearchParamsForCmpInvoiceType({
    invoiceType: tab,
    searchParams,
  });
};

export type CategoryDefaults = Pick<
  CmpInvoiceCategoryDefaultsFieldsFragment,
  | "accountNumber"
  | "accountName"
  | "taxRateLabel"
  | "taxRatePercentage"
  | "taxEnabled"
>;

export type InvoiceLineItemCatalogOption = {
  code: string;
  id: string;
  name: string;
  priceInCents: number;
  taxRatePercentage?: number;
};

// Sentinel option id for a free-entry ("Custom") line item.
export const CUSTOM_LINE_OPTION_ID = "__custom__";

let lineItemIdCounter = 0;

export const makeLineItemId = () => {
  lineItemIdCounter += 1;
  return `line-${Date.now()}-${lineItemIdCounter}-${Math.round(
    Math.random() * 1_000_000,
  )}`;
};

export const formatTaxOptionLabel = (label: string, ratePercentage: number) =>
  `${(label || "").trim() || "Tax"} (${ratePercentage}%)`;

// TICK-10 (B5): the totals row and email preview show the same "Tax (rate%)"
// shape as the tax dropdown. Defensive guard: a backend label that already
// carries a "(...)" suffix (or a zero/unset rate) renders as-is so the suffix
// is never doubled.
export const formatTaxSummaryLabel = (
  label?: string | null,
  ratePercentage?: number | null,
) => {
  const trimmedLabel = (label || "").trim() || "Tax";

  if (trimmedLabel.includes("(") || !ratePercentage || ratePercentage <= 0) {
    return trimmedLabel;
  }

  return formatTaxOptionLabel(trimmedLabel, ratePercentage);
};

export const getCategoryDefaults = (
  invoiceDefaults:
    | {
        advertising: CategoryDefaults;
        custom: CategoryDefaults;
      }
    | null
    | undefined,
  tab: InvoiceTab,
): CategoryDefaults | null => {
  if (!invoiceDefaults) {
    return null;
  }

  return tab === "advertising"
    ? invoiceDefaults.advertising
    : invoiceDefaults.custom;
};

export const createEmptyLineItem = ({
  accountNumber,
  defaultTaxRatePercentage,
}: {
  accountNumber?: string;
  defaultTaxRatePercentage?: number;
}): MockInvoiceLineItem => ({
  id: makeLineItemId(),
  code: "",
  catalogOptionId: "",
  accountNumber: accountNumber || "",
  description: "",
  quantity: 1,
  unitPriceInCents: 0,
  taxRate: defaultTaxRatePercentage || 0,
});

// Selecting a catalog option fills code / description / unit price. "Custom"
// clears the catalog link and lets code + description be free-text.
export const applyCatalogOptionToLineItem = ({
  lineItem,
  optionId,
  options,
}: {
  lineItem: MockInvoiceLineItem;
  optionId: string;
  options: InvoiceLineItemCatalogOption[];
}): MockInvoiceLineItem => {
  if (optionId === CUSTOM_LINE_OPTION_ID) {
    return { ...lineItem, catalogOptionId: CUSTOM_LINE_OPTION_ID };
  }

  if (!optionId) {
    return {
      ...lineItem,
      catalogOptionId: "",
      code: "",
      description: "",
      unitPriceInCents: 0,
    };
  }

  const option = options.find((candidate) => candidate.id === optionId);

  if (!option) {
    return lineItem;
  }

  return {
    ...lineItem,
    catalogOptionId: option.id,
    code: option.code,
    description: option.name,
    unitPriceInCents: option.priceInCents,
    taxRate: option.taxRatePercentage ?? lineItem.taxRate,
  };
};

export const isCustomLineItem = (lineItem: MockInvoiceLineItem) =>
  lineItem.catalogOptionId === CUSTOM_LINE_OPTION_ID;

export const hasUnavailableCatalogOption = ({
  lineItem,
  options,
}: {
  lineItem: MockInvoiceLineItem;
  options: InvoiceLineItemCatalogOption[];
}) =>
  Boolean(lineItem.catalogOptionId) &&
  !options.some((option) => option.id === lineItem.catalogOptionId);

export const isValidInvoiceEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const getMemberAddressCorrectionVisibility = ({
  country,
  countryError,
  customerKind,
  province,
  provinceError,
  selectedMemberId,
}: {
  country: string;
  countryError?: string;
  customerKind: "member" | "non-member";
  province: string;
  provinceError?: string;
  selectedMemberId: string;
}) => {
  const hasSelectedMember =
    customerKind === "member" && Boolean(selectedMemberId.trim());

  return {
    showCountry:
      hasSelectedMember && Boolean(!country.trim() || countryError?.trim()),
    showProvince:
      hasSelectedMember && Boolean(!province.trim() || provinceError?.trim()),
  };
};

export const isPristineEmptyLineItem = (lineItem: MockInvoiceLineItem) =>
  !lineItem.code &&
  !lineItem.catalogOptionId &&
  !lineItem.accountNumber &&
  !lineItem.description &&
  lineItem.quantity === 1 &&
  lineItem.unitPriceInCents === 0 &&
  lineItem.taxRate === 0;

export type InvoiceLineItemField = "code" | "description" | "quantity" | "unitPrice";

export const getInvoiceLineItemErrorKey = (
  lineItemId: string,
  field: InvoiceLineItemField,
) => `lineItems.${lineItemId}.${field}`;

/**
 * Design requiredValid (D:1539535): each Custom and Advertising line needs a
 * code, a description, a quantity above 0 and a unit price above $0.00. The
 * keys follow the column order, so the first error is the first red field.
 */
export const getInvoiceLineItemErrors = (
  lineItems: ReadonlyArray<MockInvoiceLineItem>,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  lineItems.forEach((item) => {
    if (!item.code?.trim()) {
      errors[getInvoiceLineItemErrorKey(item.id, "code")] =
        "Enter a code for this line.";
    }
    if (!item.description.trim()) {
      errors[getInvoiceLineItemErrorKey(item.id, "description")] =
        "Select or enter a description for this line.";
    }
    if (!(item.quantity > 0)) {
      errors[getInvoiceLineItemErrorKey(item.id, "quantity")] =
        "Enter a quantity greater than 0.";
    }
    if (!(item.unitPriceInCents > 0)) {
      errors[getInvoiceLineItemErrorKey(item.id, "unitPrice")] =
        "Enter a unit price greater than $0.00.";
    }
  });
  return errors;
};

export const parseCurrencyToCents = (value: string) => {
  // A negative input is never a valid price/amount — clamp to 0 rather than
  // silently flipping the sign by stripping the leading "-".
  if (value.trim().startsWith("-")) {
    return 0;
  }

  return Math.max(
    0,
    Math.round((Number(value.replace(/[^0-9.]/g, "")) || 0) * 100),
  );
};
